import React from 'react';

const CHART_HEIGHT = 120;
const CHART_PAD = { top: 8, right: 4, bottom: 20, left: 4 };

export const AreaUsageChart = ({ data = [], color = '#8b5cf6', testId = 'usage-area-chart' }) => {
  const points = Array.isArray(data) ? data : [];
  const width = 100;
  const innerW = width - CHART_PAD.left - CHART_PAD.right;
  const innerH = CHART_HEIGHT - CHART_PAD.top - CHART_PAD.bottom;
  const maxVal = Math.max(1, ...points.map((d) => Number(d.tokens) || 0));

  const coords = points.map((d, i) => {
    const x = CHART_PAD.left + (points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = CHART_PAD.top + innerH - ((Number(d.tokens) || 0) / maxVal) * innerH;
    return { x, y, ...d };
  });

  const linePath = coords.length
    ? coords.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')
    : '';
  const areaPath = coords.length
    ? `${linePath} L ${coords[coords.length - 1].x.toFixed(2)} ${CHART_PAD.top + innerH} L ${coords[0].x.toFixed(2)} ${CHART_PAD.top + innerH} Z`
    : '';

  const last = points[points.length - 1];
  const first = points[0];

  return (
    <div className="w-full" data-testid={testId}>
      <svg viewBox={`0 0 ${width} ${CHART_HEIGHT}`} className="w-full h-[120px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {areaPath ? <path d={areaPath} fill={`url(#grad-${testId})`} /> : null}
        {linePath ? <path d={linePath} fill="none" stroke={color} strokeWidth="0.6" vectorEffect="non-scaling-stroke" /> : null}
      </svg>
      <div className="flex justify-between text-[10px] text-zinc-600 mt-1 px-1">
        <span>{first?.date ? first.date.slice(5) : '—'}</span>
        <span>{last?.date ? last.date.slice(5) : '—'}</span>
      </div>
    </div>
  );
};

export const ModuleBarChart = ({ items = [], testId = 'module-bar-chart' }) => {
  const rows = Array.isArray(items) ? items.filter((r) => r.tokens > 0) : [];
  const maxVal = Math.max(1, ...rows.map((r) => r.tokens));

  const TONE_BAR = {
    violet: 'bg-violet-500',
    emerald: 'bg-emerald-500',
    green: 'bg-green-500',
    sky: 'bg-sky-500',
    rose: 'bg-rose-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    blue: 'bg-blue-500',
    teal: 'bg-teal-500',
    cyan: 'bg-cyan-500',
    orange: 'bg-orange-500',
    zinc: 'bg-zinc-500',
  };

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-800 py-10 text-center text-xs text-zinc-500" data-testid={testId}>
        No token usage in this period yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testId}>
      {rows.map((row) => {
        const pct = Math.round((row.tokens / maxVal) * 100);
        const bar = TONE_BAR[row.tone] || TONE_BAR.violet;
        return (
          <div key={row.module_id}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-zinc-300 truncate pr-2">{row.name}</span>
              <span className="text-zinc-500 shrink-0">{row.tokens} tokens</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-800/80 overflow-hidden">
              <div
                className={`h-full rounded-full ${bar} transition-all duration-700`}
                style={{ width: `${Math.max(pct, 4)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DonutChart = ({ segments = [], size = 88, testId = 'token-donut' }) => {
  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0) || 1;
  const r = 36;
  const cx = 44;
  const cy = 44;
  let angle = -90;

  const colors = ['#8b5cf6', '#10b981', '#f43f5e', '#f59e0b', '#06b6d4', '#6366f1'];

  const arcs = segments.map((seg, i) => {
    const value = Number(seg.value) || 0;
    const sweep = (value / total) * 360;
    const start = angle;
    angle += sweep;
    const end = angle;
    const large = sweep > 180 ? 1 : 0;
    const rad = (deg) => (deg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(rad(start));
    const y1 = cy + r * Math.sin(rad(start));
    const x2 = cx + r * Math.cos(rad(end));
    const y2 = cy + r * Math.sin(rad(end));
    if (value <= 0) return null;
    return (
      <path
        key={seg.label}
        d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
        fill={colors[i % colors.length]}
        opacity="0.9"
      />
    );
  }).filter(Boolean);

  return (
    <svg width={size} height={size} viewBox="0 0 88 88" data-testid={testId}>
      {arcs}
      <circle cx={cx} cy={cy} r={22} fill="#0a0a0a" />
    </svg>
  );
};
